import type { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import type { ConfigResponse, PresenceState, RealtimeEvent, WsClientMessage, WsServerMessage } from '@chromarble/shared-types';

export type RealtimeStatus = 'connecting' | 'open' | 'closed';

export interface TopicHandlers {
  onEvent?(event: RealtimeEvent): void;
  onPresence?(presences: PresenceState[]): void;
  /**
   * The subscription became active (first join and every re-join). Events sent before this
   * were not received, so callers resync from the API here.
   */
  onSubscribed?(): void;
}

/**
 * Receive-only realtime channel. Clients subscribe to server events and track their own
 * presence; they never publish game data (the server/RLS would reject it anyway).
 */
export interface RealtimeClient {
  readonly status: RealtimeStatus;
  subscribe(topic: string, handlers: TopicHandlers, presence?: PresenceState): () => void;
  onStatus(listener: (status: RealtimeStatus) => void): () => void;
  close(): void;
}

abstract class BaseRealtime {
  private statusValue: RealtimeStatus = 'connecting';
  private readonly statusListeners = new Set<(s: RealtimeStatus) => void>();

  get status(): RealtimeStatus {
    return this.statusValue;
  }

  protected setStatus(s: RealtimeStatus): void {
    if (s === this.statusValue) return;
    this.statusValue = s;
    this.statusListeners.forEach((l) => l(s));
  }

  onStatus(listener: (status: RealtimeStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }
}

interface Sub {
  handlers: Set<TopicHandlers>;
  presence?: PresenceState;
}

/** Node host WebSocket hub (development and self-hosted deployments). */
class LocalWsRealtime extends BaseRealtime implements RealtimeClient {
  private ws: WebSocket | null = null;
  private authed = false;
  private closed = false;
  private retry = 0;
  private readonly subs = new Map<string, Sub>();
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly onOnline = () => this.reconnectNow();

  constructor(
    private readonly url: string,
    private readonly token: string,
  ) {
    super();
    window.addEventListener('online', this.onOnline);
    this.connect();
  }

  private send(msg: WsClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  private connect(): void {
    if (this.closed) return;
    this.setStatus('connecting');
    this.authed = false;
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => this.send({ op: 'auth', token: this.token });
    ws.onmessage = (e) => this.onMessage(JSON.parse(String(e.data)) as WsServerMessage);
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.authed = false;
      if (this.pingTimer) clearInterval(this.pingTimer);
      if (this.closed) return;
      this.setStatus('closed');
      const delay = Math.min(5_000, 400 * 2 ** this.retry++);
      this.reconnectTimer = setTimeout(() => this.connect(), delay);
    };
    ws.onerror = () => ws.close();
  }

  private reconnectNow(): void {
    if (this.closed || this.authed) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.retry = 0;
    this.ws?.close();
    this.connect();
  }

  private onMessage(msg: WsServerMessage): void {
    switch (msg.op) {
      case 'authed':
        this.authed = true;
        this.retry = 0;
        this.setStatus('open');
        for (const topic of this.subs.keys()) this.send({ op: 'subscribe', topic, ref: topic });
        if (this.pingTimer) clearInterval(this.pingTimer);
        this.pingTimer = setInterval(() => this.send({ op: 'ping' }), 20_000);
        break;
      case 'subscribed': {
        const sub = this.subs.get(msg.topic);
        if (sub?.presence) this.send({ op: 'presence', topic: msg.topic, state: sub.presence });
        sub?.handlers.forEach((h) => h.onSubscribed?.());
        break;
      }
      case 'event':
        this.subs.get(msg.topic)?.handlers.forEach((h) => h.onEvent?.(msg.event));
        break;
      case 'presence_sync':
        this.subs.get(msg.topic)?.handlers.forEach((h) => h.onPresence?.(msg.presences));
        break;
      default:
        break;
    }
  }

  subscribe(topic: string, handlers: TopicHandlers, presence?: PresenceState): () => void {
    let sub = this.subs.get(topic);
    if (!sub) {
      sub = { handlers: new Set(), presence };
      this.subs.set(topic, sub);
      if (this.authed) this.send({ op: 'subscribe', topic, ref: topic });
    } else if (presence) {
      sub.presence = presence;
      if (this.authed) this.send({ op: 'presence', topic, state: presence });
    }
    sub.handlers.add(handlers);
    return () => {
      const s = this.subs.get(topic);
      if (!s) return;
      s.handlers.delete(handlers);
      if (s.handlers.size === 0) {
        this.subs.delete(topic);
        this.send({ op: 'unsubscribe', topic });
      }
    };
  }

  close(): void {
    this.closed = true;
    window.removeEventListener('online', this.onOnline);
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws?.close();
    this.setStatus('closed');
  }
}

/**
 * Supabase Realtime private channels (production).
 *
 * One socket (client) per topic: on the staging project a socket that had already authorized
 * one private topic was refused ("Unauthorized") on a second private topic, even after leaving
 * the first, while a fresh socket was accepted (docs/deployment.md, staging findings). A client
 * holds at most two topics (room + match), so this costs at most two sockets, and a reconnect
 * only ever re-joins the socket's single topic.
 */
class SupabaseRealtime extends BaseRealtime implements RealtimeClient {
  private readonly channels = new Map<string, { client: SupabaseClient; channel: RealtimeChannel; sub: Sub }>();

  constructor(
    private readonly createSocket: () => SupabaseClient,
    private readonly token: string,
  ) {
    super();
  }

  subscribe(topic: string, handlers: TopicHandlers, presence?: PresenceState): () => void {
    let entry = this.channels.get(topic);
    if (!entry) {
      const sub: Sub = { handlers: new Set(), presence };
      const client = this.createSocket();
      const channel = client.channel(topic, {
        config: { private: true, broadcast: { self: false }, presence: { key: presence?.userId ?? '' } },
      });
      channel.on('broadcast', { event: '*' }, (msg) => sub.handlers.forEach((h) => h.onEvent?.(msg.payload as RealtimeEvent)));
      channel.on('presence', { event: 'sync' }, () => {
        const list = Object.values(channel.presenceState()).flat() as unknown as PresenceState[];
        sub.handlers.forEach((h) => h.onPresence?.(list));
      });
      // The private join must carry the game token, so authorize the socket before joining.
      void client.realtime.setAuth(this.token).then(() =>
        channel.subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            this.setStatus('open');
            if (sub.presence) void channel.track(sub.presence);
            sub.handlers.forEach((h) => h.onSubscribed?.());
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            this.setStatus('connecting');
          } else if (status === 'CLOSED') {
            this.setStatus('closed');
          }
        }),
      );
      entry = { client, channel, sub };
      this.channels.set(topic, entry);
    }
    entry.sub.handlers.add(handlers);
    return () => {
      const e = this.channels.get(topic);
      if (!e) return;
      e.sub.handlers.delete(handlers);
      if (e.sub.handlers.size === 0) {
        this.channels.delete(topic);
        // Removing the socket's only channel also disconnects that socket.
        void e.client.removeChannel(e.channel);
      }
    };
  }

  close(): void {
    for (const { client, channel } of this.channels.values()) void client.removeChannel(channel);
    this.channels.clear();
    this.setStatus('closed');
  }
}

export async function createRealtimeClient(config: ConfigResponse, token: string): Promise<RealtimeClient> {
  const { supabaseUrl, supabaseAnonKey } = config.realtime;
  if (config.realtime.mode === 'supabase' && supabaseUrl && supabaseAnonKey) {
    const { createClient } = await import('@supabase/supabase-js');
    const createSocket = () =>
      createClient(supabaseUrl, supabaseAnonKey, { auth: { persistSession: false, autoRefreshToken: false } });
    return new SupabaseRealtime(createSocket, token);
  }
  const explicit = import.meta.env.VITE_REALTIME_URL as string | undefined;
  const url = explicit || `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/realtime`;
  return new LocalWsRealtime(url, token);
}
